import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Spinner } from "reactstrap"
import { get } from "helpers/api_helper"
import { Person } from "components/ReviewAudit/parts"
import { fetchAnalyticsExport } from "../ExtendedAnalytics/api"
import { exportErrorMessage, triggerDownload } from "../ExtendedAnalytics/ExportCurrentView"

// „Player texts” on the Analysis tab (sportya-api docs/tournament-feedback.md, „Textele jucătorilor”): the messages and
// „Something else” texts of the snapshot on screen, a page at a time, and „Download CSV” for the whole selection.
const TEXTS = "/admin/analytics/tournament-feedback/texts"
const LIMIT = 20
const SHOW = [["all", "All texts"], ["messages", "Messages"], ["other", "„Something else” texts"]]
const SAVED = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Bucharest", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
const saved = iso => {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? "—" : SAVED.format(date)
}
const refusedSnapshot = error => error?.response?.status === 409 && error.response.data?.data?.action === "refresh-analytics"

// `snapshot` is the view on screen; `query` its selection. A refused snapshot (409, „refresh-analytics”) makes the page
// refresh the view (`onRefresh`) and read again from the new snapshot, once per snapshot: a new one refused too is an error.
export default function PlayerTexts({ query, snapshot, onRefresh, onAccessRequired }) {
  const [show, setShow] = useState("all")
  const [paging, setPaging] = useState({ snapshot, page: 1 })
  const page = paging.snapshot === snapshot ? paging.page : 1
  const [attempt, setAttempt] = useState(0)
  const request = useMemo(() => ({ snapshot, show, page, attempt }), [snapshot, show, page, attempt])
  // `read` belongs to `request`; rows of another page, filter or snapshot are never shown under this one.
  const [read, setRead] = useState({ request: null })
  const [download, setDownload] = useState({ busy: false })
  const refreshedFor = useRef(null)

  useEffect(() => {
    let active = true
    const snapshotId = request.snapshot.metadata.snapshotId
    get(TEXTS, { params: { ...query, snapshotId, show: request.show, page: request.page, limit: LIMIT } }).then(response => {
      if (active) setRead({ request, data: response.data })
    }).catch(error => {
      if (!active) return
      if ([401, 403].includes(error?.response?.status)) onAccessRequired()
      else if (refusedSnapshot(error) && refreshedFor.current !== snapshotId) {
        refreshedFor.current = snapshotId
        onRefresh()
      } else setRead({ request, error: true })
    })
    return () => { active = false }
  }, [request, query, onRefresh, onAccessRequired])

  const current = read.request === request ? read : {}
  const data = current.data
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1
  const goTo = next => setPaging({ snapshot, page: next })
  const retry = () => { refreshedFor.current = null; setAttempt(value => value + 1) }

  const downloadCsv = async () => {
    if (download.busy || !data) return
    setDownload({ busy: true })
    let denied = false
    try {
      const response = await fetchAnalyticsExport({ ...query, snapshotId: snapshot.metadata.snapshotId, format: "csv",
        table: data.csv?.table || "feedback:texts", show })
      setDownload({ busy: false, status: `Download ready: ${triggerDownload(response, `sportya-analytics-${snapshot.view}-feedback-texts.csv`)}` })
    } catch (error) {
      denied = [401, 403].includes(error?.response?.status)
      setDownload({ busy: false, error: exportErrorMessage(error) })
    }
    if (denied) onAccessRequired()
  }

  return <section className="card" aria-labelledby="feedback-texts-title"><div className="card-body">
    <div className="tournament-feedback-analysis__texts-head">
      <div>
        <h3 className="font-size-16" id="feedback-texts-title">Player texts</h3>
        <p className="text-muted mb-2">Messages and „Something else” texts from the feedback saved in the period, newest first. Texts of deleted accounts are not shown.</p>
      </div>
      <div className="tournament-feedback-analysis__texts-actions">
        <label><span className="d-block small text-muted">Show</span>
          <select className="custom-select custom-select-sm" value={show} onChange={event => { setShow(event.target.value); goTo(1) }}>
            {SHOW.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={downloadCsv} disabled={!data?.total || download.busy}>
          <i className="bx bx-download mr-1" aria-hidden="true" />Download CSV
        </button>
      </div>
    </div>
    {download.status && <p className="small text-muted" role="status">{download.status}</p>}
    {download.error && <p className="small text-danger" role="alert">{download.error}</p>}
    {current.error && <div role="alert" className="my-3">
      <p className="mb-2">The player texts could not be loaded.</p>
      <button type="button" className="btn btn-outline-primary btn-sm" onClick={retry}>Try again</button>
    </div>}
    {!data && !current.error && <div className="text-center my-3"><Spinner size="sm" color="primary" /></div>}
    {data && <>
      <div className="table-responsive"><table className="table table-sm mb-0">
        <thead><tr><th>Saved</th><th>Text</th><th>Where</th><th>Tournament</th><th>Organizer</th><th>Sport</th><th>Player</th></tr></thead>
        <tbody>{data.results.map(row => <tr key={row.id}>
          <td className="text-nowrap">{saved(row.savedAt)}</td>
          <td className="tournament-feedback-analysis__text">„{row.text}”</td>
          <td>{row.where.label}</td>
          <td><Link to={`/events/${row.tournament.eventId}`}>{row.tournament.name || row.tournament.eventId}</Link></td>
          <td>{row.organizer?.name || "—"}</td>
          <td>{row.sportType}</td>
          <td><Person person={row.player} id={row.player?.userId} /></td>
        </tr>)}</tbody>
      </table></div>
      {!data.results.length && <p className="text-muted mt-3 mb-0">No player texts in this selection.</p>}
      {data.total > 0 && <div className="tournament-feedback-analysis__pages">
        <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => goTo(page - 1)}>Previous</button>
        <span className="small">Page {page} of {pages} · {data.total} texts</span>
        <button type="button" className="btn btn-secondary btn-sm" disabled={page >= pages} onClick={() => goTo(page + 1)}>Next</button>
      </div>}
    </>}
  </div></section>
}
